import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setNewDesignAction } from '../../../../modules/actions';
import { useNewDesign } from '../../../../hooks/useNewDesign';
import { getIsUploadInProcess } from '../../../../modules/selectors';
import Api from '../../../../modules/utils/API';
import { ApiErrors } from '../../../../modules/utils/API/APIErrors';

export type DesignSwitchWarning = 'upload' | 'templates';

/**
 * The old design cannot show a template only the new design has, so the backend
 * moves such galleries to Simple Filigree on the way back. Unknown counts err on
 * the side of warning.
 */
const usesNewDesignOnlyTemplates = async (): Promise<boolean> => {
  try {
    const response = await Api.User.getNewDesignTemplateUsage();
    ApiErrors.checkOnApiError(response);
    const { collections, galleryPresets } = response.result;

    return collections + galleryPresets > 0;
  } catch {
    return true;
  }
};

/**
 * State for the banner that lets a photographer switch between the old and the
 * new design. Closing it only lasts until the next page load, on purpose.
 *
 * A switch reloads the page, which the browser does not always guard with its own
 * prompt, so a running upload is warned about here before anything else.
 */
export const useNewDesignBanner = () => {
  const dispatch = useDispatch();
  const isNewDesign = useNewDesign();
  const isUploadInProcess = useSelector(getIsUploadInProcess);
  const [isClosed, setIsClosed] = useState(false);
  const [warning, setWarning] = useState<DesignSwitchWarning | null>(null);
  const [isCheckingTemplates, setIsCheckingTemplates] = useState(false);

  const close = useCallback(() => setIsClosed(true), []);

  const switchDesign = useCallback(async () => {
    if (isNewDesign) {
      setIsCheckingTemplates(true);
      const needsWarning = await usesNewDesignOnlyTemplates();
      setIsCheckingTemplates(false);

      if (needsWarning) {
        setWarning('templates');

        return;
      }
    }

    dispatch(setNewDesignAction(!isNewDesign));
  }, [dispatch, isNewDesign]);

  const toggleDesign = useCallback(() => {
    if (isUploadInProcess) {
      setWarning('upload');

      return;
    }

    switchDesign();
  }, [isUploadInProcess, switchDesign]);

  const confirmWarning = useCallback(() => {
    setWarning(null);

    if (warning === 'upload') {
      switchDesign();
    } else {
      dispatch(setNewDesignAction(!isNewDesign));
    }
  }, [dispatch, isNewDesign, switchDesign, warning]);

  const cancelWarning = useCallback(() => setWarning(null), []);

  return {
    isOpen: !isClosed,
    isNewDesign,
    isCheckingTemplates,
    warning,
    close,
    toggleDesign,
    confirmWarning,
    cancelWarning
  };
};
